import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  Stack,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Tooltip,
  Box,
  Switch,
  FormControlLabel,
  Typography,
  Collapse,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Home,
  Assignment,
  Logout,
  AccountCircle,
  Print,
  Menu,
  Settings,
  LiveHelp,
  DirectionsBoat,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
} from "@mui/icons-material";
import logo from "../assets/oceanlogix.png";
import { useUserContext } from "../Contexts/UserContext";
import { useSettings } from "../Contexts/SettingsContext";
import TimezoneClock from "./TimeZoneClock";
import {
  loadingMenuOptions,
  loadingPrintableDocuments,
  dischargeMenuOptions,
  dischargePrintableDocuments,
  otherMenuOptions,
  reportMenuOptions,
} from "../constants/menuOptions";
import { getSelectedTrip } from "../utils/voyageSelection";

// 1. Byt till V2
import { useVoyageContextV2 } from "../Contexts/VoyageContextV2";

const Sidebar = ({
  onSelect,
  onLogout,
  open,
  setOpen,
  isMobile,
  setDarkMode,
  darkMode,
}) => {
  const theme = useTheme();
  const { user } = useUserContext();
  const [selected, setSelected] = useState("");
  const { timezone, is24Hour } = useSettings();

  // 2. Hämta data från V2 State
  const { currentVoyage, selectedTripId } = useVoyageContextV2();

  // 3. Resolve selected trip from current selection
  const currentTrip = getSelectedTrip(currentVoyage, selectedTripId);
  const operationType = currentTrip?.operationType ?? null;

  // Collapse states
  const [loadingOpen, setLoadingOpen] = useState(false);
  const [loadingPrintOpen, setLoadingPrintOpen] = useState(false);
  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [dischargePrintOpen, setDischargePrintOpen] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);

  useEffect(() => {
    if (operationType === "Loading") {
      setLoadingOpen(true);
      setDischargeOpen(false);
      setLoadingPrintOpen(true);
      setDischargePrintOpen(false);
    } else if (operationType === "Discharging") {
      setLoadingOpen(false);
      setDischargeOpen(true);
      setLoadingPrintOpen(false);
      setDischargePrintOpen(true);
    }
  }, [operationType]);

  const activeColor = theme.palette.primary.main;
  const iconColor = darkMode
    ? theme.palette.grey[400]
    : theme.palette.primary.dark;

  const navItemStyle = {
    mx: 1,
    borderRadius: 2,
    mb: 0.5,
    "&.Mui-selected": {
      bgcolor: alpha(activeColor, 0.12),
      "& .MuiListItemIcon-root": { color: activeColor },
      "& .MuiListItemText-primary": { fontWeight: 700, color: activeColor },
    },
  };

  const renderSection = (
    title,
    options,
    docs,
    openState,
    setOpenState,
    printOpen,
    setPrintOpen
  ) => (
    <List disablePadding>
      <ListItemButton onClick={() => setOpenState(!openState)} sx={{ mt: 1 }}>
        <ListItemIcon sx={{ color: iconColor, minWidth: 40 }}>
          <Assignment fontSize="small" />
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={title}
            primaryTypographyProps={{
              variant: "subtitle2",
              sx: {
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: 1,
                fontSize: "0.7rem",
              },
            }}
          />
        )}
        {open &&
          (openState ? (
            <ExpandLess fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          ))}
      </ListItemButton>

      <Collapse in={openState} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {options
            .filter((item) => item.roles.some((r) => user.roles.includes(r)))
            .map((item) => (
              <ListItemButton
                key={item.id}
                sx={{ ...navItemStyle, pl: 4 }}
                selected={selected === item.id}
                onClick={() => {
                  setSelected(item.id);
                  onSelect(item.id);
                  if (isMobile) setOpen(false);
                }}
              >
                <ListItemIcon sx={{ color: iconColor, minWidth: 35 }}>
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: "body2",
                      sx: { fontWeight: 500 },
                    }}
                  />
                )}
              </ListItemButton>
            ))}

          {docs && (
            <>
              <ListItemButton
                onClick={() => setPrintOpen(!printOpen)}
                sx={{ pl: 4 }}
              >
                <ListItemIcon sx={{ color: iconColor, minWidth: 35 }}>
                  <Print fontSize="small" />
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary="Printable Documents"
                    primaryTypographyProps={{
                      variant: "caption",
                      sx: { fontWeight: 700, opacity: 0.8 },
                    }}
                  />
                )}
                {open &&
                  (printOpen ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  ))}
              </ListItemButton>
              <Collapse in={printOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {docs
                    .filter((item) =>
                      item.roles.some((r) => user.roles.includes(r))
                    )
                    .map((item) => (
                      <ListItemButton
                        key={item.id}
                        sx={{ ...navItemStyle, pl: 6 }}
                        selected={selected === item.id}
                        onClick={() => {
                          setSelected(item.id);
                          onSelect(item.id);
                          if (isMobile) setOpen(false);
                        }}
                      >
                        <ListItemIcon sx={{ color: iconColor, minWidth: 35 }}>
                          {item.icon}
                        </ListItemIcon>
                        {open && (
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{ variant: "caption" }}
                          />
                        )}
                      </ListItemButton>
                    ))}
                </List>
              </Collapse>
            </>
          )}
        </List>
      </Collapse>
    </List>
  );

  return (
    <>
      {!open && (
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            left: 12,
            top: 12,
            zIndex: 1201,
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: 3,
            "&:hover": { bgcolor: theme.palette.background.paper },
          }}
        >
          <Menu />
        </IconButton>
      )}

      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: open ? 260 : 0,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 260,
            background: darkMode ? "#121212" : "#f8fafc",
            borderRight: `1px solid ${theme.palette.divider}`,
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* LOGO & CLOSE */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            minHeight: 64,
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{
              height: 28,
            }}
          />
          <IconButton onClick={() => setOpen(false)} size="small">
            <ChevronLeft />
          </IconButton>
        </Box>

        <Divider />

        {/* TIME & SETTINGS BOX */}
        <Box
          sx={{
            p: 2.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            textAlign: "center",
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{
              fontWeight: 800,
              display: "block",
              mb: 0.5,
              letterSpacing: 1,
            }}
          >
            Vessel Local Time
          </Typography>
          <TimezoneClock timezone={timezone} is24Hour={is24Hour} />

          <Box sx={{ mt: 1.5, display: "flex", justifyContent: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={darkMode}
                  onChange={() => {
                    setDarkMode(!darkMode);
                    localStorage.setItem("darkMode", !darkMode);
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {darkMode ? "DARK" : "LIGHT"}
                </Typography>
              }
            />
          </Box>
        </Box>

        <Divider />

        {/* QUICK ACTIONS */}
        <Stack direction="row" justifyContent="center" spacing={1} py={2}>
          {[
            {
              id: "account",
              icon: <AccountCircle fontSize="small" />,
              title: "Account",
            },
            {
              id: "settings",
              icon: <Settings fontSize="small" />,
              title: "Settings",
            },
            {
              id: "shipParticulars",
              icon: <DirectionsBoat fontSize="small" />,
              title: "Particulars",
            },
            { id: "faq", icon: <LiveHelp fontSize="small" />, title: "FAQ" },
          ].map((btn) => (
            <Tooltip key={btn.id} title={btn.title}>
              <IconButton
                size="small"
                onClick={() => onSelect(btn.id)}
                sx={{ bgcolor: alpha(iconColor, 0.05) }}
              >
                {btn.icon}
              </IconButton>
            </Tooltip>
          ))}
          <Tooltip title="Logout">
            <IconButton
              size="small"
              color="error"
              onClick={onLogout}
              sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}
            >
              <Logout fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Divider />

        {/* NAVIGATION CONTENT */}
        <Box sx={{ flexGrow: 1, overflowY: "auto", py: 1 }}>
          <List disablePadding>
            <ListItemButton
              selected={selected === "currentVoyage"}
              sx={navItemStyle}
              onClick={() => {
                setSelected("currentVoyage");
                onSelect("currentVoyage");
                if (isMobile) setOpen(false);
              }}
            >
              <ListItemIcon sx={{ color: iconColor, minWidth: 40 }}>
                <Home />
              </ListItemIcon>
              <ListItemText
                primary="Current Voyage"
                primaryTypographyProps={{
                  variant: "subtitle2",
                  sx: { fontWeight: 800 },
                }}
              />
            </ListItemButton>
          </List>

          <Divider sx={{ my: 1, mx: 2, borderStyle: "dashed" }} />

          {renderSection(
            "Operations (Loading)",
            loadingMenuOptions,
            loadingPrintableDocuments,
            loadingOpen,
            setLoadingOpen,
            loadingPrintOpen,
            setLoadingPrintOpen
          )}
          <Divider sx={{ my: 1, mx: 2, borderStyle: "dashed" }} />
          {renderSection(
            "Operations (Discharge)",
            dischargeMenuOptions,
            dischargePrintableDocuments,
            dischargeOpen,
            setDischargeOpen,
            dischargePrintOpen,
            setDischargePrintOpen
          )}
          <Divider sx={{ my: 1, mx: 2, borderStyle: "dashed" }} />
          {renderSection(
            "Logistics & VEF",
            otherMenuOptions,
            null,
            otherOpen,
            setOtherOpen
          )}

          <Divider sx={{ my: 1, mx: 2, borderStyle: "dashed" }} />

          <Typography
            variant="overline"
            sx={{
              px: 3,
              mt: 3,
              mb: 1,
              display: "block",
              fontWeight: 800,
              color: "text.disabled",
              letterSpacing: 1,
            }}
          >
            Reporting
          </Typography>
          <List sx={{ mb: 5 }}>
            {reportMenuOptions
              .filter((item) => item.roles.some((r) => user.roles.includes(r)))
              .map((item) => (
                <ListItemButton
                  key={item.id}
                  sx={navItemStyle}
                  selected={selected === item.id}
                  onClick={() => {
                    setSelected(item.id);
                    onSelect(item.id);
                    if (isMobile) setOpen(false);
                  }}
                >
                  <ListItemIcon sx={{ color: iconColor, minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: "body2",
                      sx: { fontWeight: 500 },
                    }}
                  />
                </ListItemButton>
              ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Sidebar;
